import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Star, Loader2, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import { getReviews, submitReview } from "@/lib/api";
import ShareFacebookButton from "@/components/ShareFacebookButton";

const REVIEW_BOOKS = [
  { id: "survival", title: "Survival" },
  { id: "youre-never-safe", title: "You're Never Safe" },
  { id: "suspicious-times", title: "Suspicious Times: Elvis and the Time Travellers" },
  { id: "dingo", title: "Dingo, He's Called" },
];

function StarRow({ value, onChange, readOnly = false, size = "w-6 h-6" }) {
  return (
    <div className="flex items-center gap-1" data-testid={readOnly ? "review-stars-display" : "review-stars-input"}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={() => onChange && onChange(n)}
          data-testid={readOnly ? undefined : `star-${n}`}
          className={`${readOnly ? "cursor-default" : "cursor-pointer transition-transform hover:scale-110"}`}
          aria-label={`${n} star`}
        >
          <Star
            className={`${size} ${n <= value ? "fill-current text-primary-gold" : "text-white/25"}`}
          />
        </button>
      ))}
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function Reviews() {
  const [searchParams] = useSearchParams();
  const initialBook = searchParams.get("book");
  const [productId, setProductId] = useState(
    REVIEW_BOOKS.some((b) => b.id === initialBook) ? initialBook : "survival"
  );
  const [name, setName] = useState("");
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [justPosted, setJustPosted] = useState(null);

  const loadReviews = async () => {
    setLoading(true);
    try {
      const data = await getReviews();
      setReviews(data);
    } catch {
      toast.error("Could not load reviews.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReviews();
  }, []);

  const visibleReviews = useMemo(
    () => reviews.filter((r) => r.product_id === productId),
    [reviews, productId]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please add your name.");
    if (rating < 1) return toast.error("Please pick a star rating.");
    if (text.trim().length < 3) return toast.error("Please write a short review.");
    setSubmitting(true);
    try {
      const data = await submitReview({ product_id: productId, name: name.trim(), rating, text: text.trim() });
      toast.success("Thank you! Your review has been posted.");
      setJustPosted(data.review);
      setName("");
      setRating(0);
      setText("");
      await loadReviews();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedTitle = REVIEW_BOOKS.find((b) => b.id === productId)?.title;

  return (
    <div className="pt-28 max-w-4xl mx-auto px-6 sm:px-8 pb-24" data-testid="reviews-page">
      <span className="font-mono-ui text-xs uppercase tracking-[0.3em] text-primary-gold">
        Reader Reviews
      </span>
      <h1 className="font-serif-display text-5xl sm:text-6xl font-semibold tracking-tight mt-6">
        Write a Review
      </h1>
      <p className="mt-6 text-soft text-lg leading-relaxed max-w-2xl">
        Read one of Michael Leckie's novels? Choose the book below, leave a star rating, and share
        your thoughts. Your review will be published for other readers to see.
      </p>

      <form
        onSubmit={handleSubmit}
        data-testid="review-form"
        className="mt-12 border border-white/10 bg-[#121212] p-6 sm:p-8 rounded-lg space-y-6"
      >
        <div>
          <label className="font-mono-ui text-xs uppercase tracking-[0.18em] text-primary-gold">
            Choose a book
          </label>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            data-testid="review-book-select"
            className="mt-3 w-full bg-[#0B0C10] border border-white/15 rounded-md px-4 py-3 text-text-primary focus:outline-none focus:border-primary-gold transition-colors"
          >
            {REVIEW_BOOKS.map((b) => (
              <option key={b.id} value={b.id}>
                {b.title}
              </option>
            ))}
          </select>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label className="font-mono-ui text-xs uppercase tracking-[0.18em] text-primary-gold">
              Your name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              placeholder="e.g. Jane from Adelaide"
              data-testid="review-name-input"
              className="mt-3 w-full bg-[#0B0C10] border border-white/15 rounded-md px-4 py-3 text-text-primary placeholder:text-soft/50 focus:outline-none focus:border-primary-gold transition-colors"
            />
          </div>
          <div>
            <label className="font-mono-ui text-xs uppercase tracking-[0.18em] text-primary-gold">
              Your rating
            </label>
            <div className="mt-4">
              <StarRow value={rating} onChange={setRating} />
            </div>
          </div>
        </div>

        <div>
          <label className="font-mono-ui text-xs uppercase tracking-[0.18em] text-primary-gold">
            Your review
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={2000}
            rows={5}
            placeholder={`What did you think of ${selectedTitle}?`}
            data-testid="review-text-input"
            className="mt-3 w-full bg-[#0B0C10] border border-white/15 rounded-md px-4 py-3 text-text-primary placeholder:text-soft/50 focus:outline-none focus:border-primary-gold transition-colors resize-none"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="review-submit-button"
          className="inline-flex items-center gap-2 bg-primary-gold text-[#0B0C10] font-mono-ui text-xs uppercase tracking-[0.18em] px-8 py-4 rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquareQuote className="w-4 h-4" />}
          Post Review
        </button>
      </form>

      {justPosted && (
        <div
          data-testid="share-posted-review-panel"
          className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-primary-gold/30 bg-primary-gold/5 rounded-lg p-5"
        >
          <div>
            <p className="text-text-primary">Your review is live — thank you!</p>
            <p className="text-soft text-sm mt-1">Tell your friends what you thought of {justPosted.product_title}.</p>
          </div>
          <div className="inline-flex items-center justify-center bg-[#1877F2] rounded-full px-6 py-3 [&_button]:text-white [&_button:hover]:text-white">
            <ShareFacebookButton review={justPosted} testId="share-posted-review-button" />
          </div>
        </div>
      )}

      <div className="mt-16 border-t border-white/10 pt-12">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <h2 className="font-serif-display text-3xl">Reviews for {selectedTitle}</h2>
          <span className="font-mono-ui text-xs uppercase tracking-[0.18em] text-soft">
            {visibleReviews.length} {visibleReviews.length === 1 ? "review" : "reviews"}
          </span>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 text-primary-gold animate-spin" />
          </div>
        ) : visibleReviews.length === 0 ? (
          <p className="mt-8 text-soft" data-testid="no-reviews-message">
            No reviews yet for this book. Be the first to share your thoughts!
          </p>
        ) : (
          <div className="mt-8 space-y-5" data-testid="reviews-list">
            {visibleReviews.map((r) => (
              <div
                key={r.id}
                data-testid="review-item"
                className="border border-white/10 bg-[#121212] rounded-lg p-6"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="font-serif-display text-lg text-text-primary">{r.name}</span>
                    <StarRow value={r.rating} readOnly size="w-4 h-4" />
                  </div>
                  <span className="font-mono-ui text-xs text-soft whitespace-nowrap">
                    {formatDate(r.created_at)}
                  </span>
                </div>
                <p className="mt-4 text-soft leading-relaxed whitespace-pre-wrap">{r.text}</p>
                <div className="mt-5 pt-4 border-t border-white/5">
                  <ShareFacebookButton review={r} label="Share" testId={`share-review-${r.id}`} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
